import React, { useRef } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  MiniMap,
  Node,
  Edge
} from 'reactflow';
import 'reactflow/dist/style.css';
import { TreeTabProps } from './types';
import api from '../../services/api';
import { Goal } from './types';

const TreeTab: React.FC<TreeTabProps> = ({
  goals,
  projects,
  loadData,
  showTreeConnectModal,
  setShowTreeConnectModal,
  treeConnectSource,
  setTreeConnectSource,
  handleTreeConnectGoal,
  onConnectNodes
}) => {
  const connectingNodeId = useRef<string | null>(null);
  
  // Первый (единственный) проект пользователя
  const project = projects && projects.length > 0 ? projects[0] : null;

  // Преобразуем реальные цели в узлы дерева
  const buildTreeLayout = (goals: Goal[], project: any) => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    
    // Если нет проекта - ничего не показываем
    if (!project) {
      return { nodes, edges };
    }

    // Добавляем узел проекта как корневую точку
    nodes.push({
      id: 'project-' + project.id,
      type: 'default',
      position: { x: 400, y: 600 },
      draggable: false,
      sourcePosition: 'top' as any,
      data: {
        label: (
          <div style={{ 
            padding: '15px 20px', 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            color: 'white',
            minWidth: '200px'
          }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '5px' }}>
              🎯 {project.name}
            </div>
            <div style={{ fontSize: '11px', opacity: 0.8 }}>
              Корневой проект
            </div>
          </div>
        )
      },
      style: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        width: 240
      }
    });
    
    // Теперь строим дерево целей ниже проекта
    const rootGoals = goals.filter(g => !g.parentGoalId && g.projectId === project.id);
    
    if (rootGoals.length === 0) {
      return { nodes, edges };
    }
    const levelWidth = 250;
    const levelHeight = 220;
    
    // Функция для расчета максимальной глубины дерева от данной цели
    const calculateMaxDepth = (goalId: number): number => {
      const children = goals.filter((g: Goal) => g.parentGoalId === goalId);
      if (children.length === 0) return 0;
      return 1 + Math.max(...children.map((child: Goal) => calculateMaxDepth(child.id)));
    };

    // Функция для расчета ширины поддерева (сколько листьев)
    const calculateTreeWidth = (goalId: number): number => {
      const children = goals.filter((g: Goal) => g.parentGoalId === goalId);
      if (children.length === 0) return 1; // Лист занимает 1 позицию
      // Ширина = сумма ширин всех детей
      return children.reduce((sum, child) => sum + calculateTreeWidth(child.id), 0);
    };

    const getGoalColor = (goal: Goal) => {
      if (goal.status === 'completed' || (goal.progress && goal.progress === 100)) {
        return { border: '#10b981', bg: '#f0fdf4', text: '#10b981' };
      } else if (goal.progress && goal.progress > 0) {
        return { border: '#f59e0b', bg: '#fff', text: '#f59e0b' };
      } else {
        return { border: '#d1d5db', bg: '#fff', text: '#6b7280' };
      }
    };

    const buildTree = (goal: Goal, x: number, y: number, level: number, siblingIndex: number, totalSiblings: number, maxDepth: number) => {
      const colors = getGoalColor(goal);
      const progress = goal.progress || 0;
      const stepCount = goal.steps?.length || 0;
      
      const isRoot = level === 0;
      const nodeWidth = isRoot ? 220 : (level === 1 ? 180 : 150);
      const borderWidth = isRoot ? 4 : (level === 1 ? 3 : 2);
      
      const hasChildren = goals.some(g => g.parentGoalId === goal.id);
      const isRootGoal = level === 0; // Корневая цель (любая первая)
      
      // Цели растут вверх: выход сверху к родителю, вход снизу от детей
      const nodeConfig: any = {
        id: String(goal.id),
        type: 'default',
        position: { x, y },
        draggable: true,
        sourcePosition: 'top' as any,
        targetPosition: 'bottom' as any,
        data: {
          label: (
            <div style={{ padding: isRoot ? '12px' : (level === 1 ? '10px' : '8px'), textAlign: 'center', position: 'relative' }}>
              {!hasChildren && (
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (window.confirm(`Отвязать цель "${goal.name}" от дерева?`)) {
                      try {
                        await api.put(`/api/goals/${goal.id}`, {
                          parentGoalId: null,
                          projectId: null
                        });
                        await loadData();
                      } catch (error) {
                        console.error('Ошибка при отвязке цели:', error);
                        alert('❌ Ошибка при отвязке цели');
                      }
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    lineHeight: 1
                  }}
                  title="Отвязать от дерева"
                >
                  ×
                </button>
              )}
              
              <div style={{ 
                fontWeight: 'bold', 
                fontSize: isRoot ? '15px' : (level === 1 ? '13px' : '12px'),
                marginBottom: '4px' 
              }}>
                {isRoot ? '🌳' : (progress === 100 ? '✅' : progress > 0 ? '🔄' : '⭕')} {goal.name}
              </div>
              {stepCount > 0 && (
                <>
                  <div style={{ fontSize: '10px', color: '#6b7280' }}>{stepCount} шагов</div>
                  <div style={{ 
                    width: isRoot ? '120px' : '90px', 
                    height: isRoot ? '5px' : '4px', 
                    background: '#e5e7eb', 
                    borderRadius: '2px',
                    margin: '5px auto',
                    overflow: 'hidden'
                  }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: colors.text }}></div>
                  </div>
                  <div style={{ fontSize: '10px', color: colors.text }}>
                    {progress === 100 ? '✓' : ''} {progress}%
                  </div>
                </>
              )}
            </div>
          )
        },
        style: {
          background: colors.bg,
          border: `${borderWidth}px solid ${colors.border}`,
          borderRadius: isRoot ? '12px' : '8px',
          padding: 0,
          width: nodeWidth
        }
      };
      
      nodes.push(nodeConfig);

      const children = goals.filter((g: Goal) => g.parentGoalId === goal.id);
      
      if (children.length > 0) {
        const childY = y - levelHeight;
        
        // Рассчитываем ширину каждого поддерева
        const childWidths = children.map(child => calculateTreeWidth(child.id));
        const totalWidth = childWidths.reduce((sum, w) => sum + w, 0);
        const totalPixelWidth = totalWidth * levelWidth;
        
        // Начальная позиция - центрируем относительно родителя
        let currentX = x - totalPixelWidth / 2 + (levelWidth / 2);

        children.forEach((child: Goal, index: number) => {
          const childWidth = childWidths[index];
          // Центр поддерева ребёнка
          const childX = currentX + (childWidth * levelWidth) / 2 - (levelWidth / 2);
          
          const childColors = getGoalColor(child);
          const branchColor = childColors.border === '#10b981' ? '#228B22' : '#8B4513';
          
          // Толщина ветки: чем ближе к корню (level 0), тем толще
          // maxDepth - это максимальная глубина от корня
          // level - текущий уровень (0 = корень)
          // Формула: базовая толщина + (maxDepth - level) пикселей
          const depthFactor = maxDepth - level;
          const branchWidth = Math.max(1, 2 + depthFactor * 1.5);
          
          edges.push({
            id: `e${goal.id}-${child.id}`,
            source: String(goal.id),
            target: String(child.id),
            type: 'default',
            animated: false,
            style: {
              stroke: branchColor,
              strokeWidth: branchWidth,
              strokeLinecap: 'round'
            }
          });

          buildTree(child, childX, childY, level + 1, index, children.length, maxDepth);
          
          // Переходим к следующему поддереву
          currentX += childWidth * levelWidth;
        });
      }
    };

    // Строим дерево от корневых целей (привязанных к проекту) - растут вверх от проекта
    if (rootGoals.length > 0) {
      const totalRoots = rootGoals.length;
      
      // Рассчитываем ширину каждого корневого дерева
      const rootWidths = rootGoals.map(root => calculateTreeWidth(root.id));
      const totalWidth = rootWidths.reduce((sum, w) => sum + w, 0);
      const totalPixelWidth = totalWidth * levelWidth;
      
      let currentX = 400 - totalPixelWidth / 2 + (levelWidth / 2);
      
      rootGoals.forEach((rootGoal, index) => {
        const maxDepth = calculateMaxDepth(rootGoal.id);
        const rootWidth = rootWidths[index];
        const x = currentX + (rootWidth * levelWidth) / 2 - (levelWidth / 2);
        
        buildTree(rootGoal, x, 400, 0, index, totalRoots, maxDepth);
        
        // Соединяем корневую цель с узлом проекта
        // Толщина зависит от глубины дерева (как и у остальных веток)
        const branchWidth = Math.max(1, 2 + maxDepth * 1.5);
        
        edges.push({
          id: `e-project-${rootGoal.id}`,
          source: 'project-' + project.id,
          target: String(rootGoal.id),
          type: 'default',
          animated: false,
          style: {
            stroke: '#8B4513',
            strokeWidth: branchWidth,
            strokeLinecap: 'round'
          }
        });
        
        // Переходим к следующему корневому дереву
        currentX += rootWidth * levelWidth;
      });
    }

    return { nodes, edges };
  };

  const { nodes: realNodes, edges: realEdges } = buildTreeLayout(goals, project);

  return (
    <div>
      {/* React Flow Canvas */}
      <style>{`
        .react-flow__handle {
          width: 16px !important;
          height: 16px !important;
          border-radius: 50% !important;
          background: #3b82f6 !important;
          border: 3px solid white !important;
          box-shadow: 0 0 0 2px #3b82f6 !important;
        }
        .react-flow__handle:hover {
          width: 20px !important;
          height: 20px !important;
          background: #2563eb !important;
          box-shadow: 0 0 0 3px #2563eb !important;
        }
      `}</style>
      {project && (
        <div style={{
          padding: '15px 20px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          marginBottom: '15px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', marginBottom: '5px' }}>
              🎯 {project.name}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
              Корневой проект
            </div>
          </div>
          <button
            onClick={async () => {
              const newName = prompt('Переименовать проект:', project.name);
              if (newName && newName.trim() && newName !== project.name) {
                try {
                  await api.put(`/api/projects/${project.id}`, {
                    name: newName.trim()
                  });
                  await loadData();
                } catch (err) {
                  console.error('Ошибка переименования проекта:', err);
                  alert('❌ Ошибка при переименовании проекта');
                }
              }
            }}
            style={{
              padding: '8px 16px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
            }}
          >
            ✏️ Переименовать
          </button>
        </div>
      )}
      <div style={{ 
        width: '100%', 
        height: '700px', 
        background: 'linear-gradient(to bottom, #87CEEB 0%, #E0F6FF 40%, #90EE90 70%, #8B4513 100%)',
        border: '2px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {!project ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🌳</div>
            <h3 style={{ margin: '10px 0' }}>Создайте свой проект</h3>
            <p style={{ margin: '5px 0', marginBottom: '20px' }}>
              Это может быть "Моя жизнь", "Стартап", "Бизнес" - как вам удобно
            </p>
            <button
              onClick={async () => {
                const name = prompt('Название проекта:', 'Моя жизнь');
                if (name && name.trim()) {
                  try {
                    await api.post('/api/projects', {
                      name: name.trim()
                    });
                    await loadData();
                  } catch (err) {
                    console.error('Ошибка создания проекта:', err);
                    alert('❌ Ошибка при создании проекта');
                  }
                }
              }}
              style={{
                padding: '12px 24px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              🌱 Создать проект
            </button>
          </div>
        ) : (
          <ReactFlow
            nodes={realNodes}
            edges={realEdges}
            onConnect={onConnectNodes}
            onConnectStart={(event, params) => {
              if (params.nodeId) {
                connectingNodeId.current = params.nodeId;
                console.log('Начало соединения от узла:', params.nodeId);
              }
            }}
            onConnectEnd={(event) => {
              const target = event.target as HTMLElement;
              const isNode = target.closest('.react-flow__node');
              
              if (!isNode && connectingNodeId.current) {
                console.log('Соединение завершено в пустоте - показываем модалку');
                
                // Если тянем от проекта - извлекаем ID проекта
                if (connectingNodeId.current.startsWith('project-')) {
                  const projectId = connectingNodeId.current.replace('project-', '');
                  console.log('Тянем от проекта, показываем модалку с целями');
                  // Используем специальное значение для проекта
                  setTreeConnectSource(-1);
                  setShowTreeConnectModal(true);
                } else {
                  // Обычная цель
                  setTreeConnectSource(Number(connectingNodeId.current));
                  setShowTreeConnectModal(true);
                }
              }
              
              connectingNodeId.current = null;
            }}
            fitView
            attributionPosition="bottom-left"
            style={{ background: 'transparent' }}
          >
            <Background 
              color="rgba(139, 69, 19, 0.1)" 
              gap={20}
            />
            <Controls />
            <MiniMap 
              nodeColor={(node) => {
                const border = String(node.style?.border || '');
                if (border.includes('#10b981')) return '#10b981';
                if (border.includes('#f59e0b')) return '#f59e0b';
                if (border.includes('#3b82f6')) return '#3b82f6';
                return '#d1d5db';
              }}
              maskColor="rgba(0, 0, 0, 0.1)"
            />
          </ReactFlow>
        )}
      </div>

      {/* Модалка для подключения цели к дереву */}
      {showTreeConnectModal && (
        <div className="mobile-dialog-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>🌿 Привязать цель к дереву</h3>
            <p style={{ color: '#6b7280' }}>
              Выберите цель без родителя, чтобы добавить её как подцель:
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflowY: 'auto', marginBottom: '20px' }}>
              {goals
                .filter(g => !g.parentGoalId && !g.projectId && g.id !== treeConnectSource)
                .length > 0 ? (
                  goals
                    .filter(g => !g.parentGoalId && !g.projectId && g.id !== treeConnectSource)
                    .map(goal => (
                      <button
                        key={goal.id}
                        onClick={() => handleTreeConnectGoal(goal.id, goals, project?.id)}
                        style={{
                          padding: '12px',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '14px'
                        }}
                      >
                        🎯 {goal.name}
                      </button>
                    ))
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                    Нет доступных целей для привязки.<br />
                    Все цели уже в дереве или создайте новую цель.
                  </p>
                )}
            </div>
            
            <button
              onClick={() => {
                setShowTreeConnectModal(false);
                setTreeConnectSource(null);
              }}
              style={{
                padding: '10px 20px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        background: '#f0f9ff', 
        borderRadius: '8px',
        border: '1px solid #bfdbfe'
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '14px', color: '#1e40af' }}>
          💡 Как пользоваться деревом целей:
        </h4>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
          <li><strong>"Жизнь"</strong> - корневая цель, от неё растёт всё дерево</li>
          <li><strong>Привязать цель:</strong> потяните от узла (сверху) к пустому месту → выберите цель из списка</li>
          <li><strong>Отвязать цель:</strong> нажмите × на "листе" (цели без детей)</li>
          <li><strong>Визуализация:</strong> толщина веток = количество шагов, цвет = статус (🟢 завершено, 🟠 в процессе, ⚪ не начато)</li>
        </ul>
      </div>
    </div>
  );
};

export default TreeTab;
